import React, { useState, useCallback, useRef } from 'react';
import Modal from 'react-modal';
import { useMutation } from '@apollo/react-hooks';
import { uploadNutritionPdfMutation } from '@witness/graphql';
import styled from 'styled-components';

import CustomButton from '../button/CustomButton';
import ICONS from '../../constants/icons';

const customStyles = {
  overlay: {
    backgroundColor: '#05000066',
    zIndex: 44444445,
  },
  content: {
    background: 'transparent',
    zIndex: 44444446,
    width: '620px',
    maxWidth: '90%',
    minWidth: '300px',
    height: 'fit-content',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: '#001212',
    borderRadius: '12px',
    border: 'none',
    padding: '0',
    bottom: 'auto',
    right: 'auto',
  },
};

const Wrapper = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  overflow: hidden;
  min-height: 360px;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2.4rem 2.8rem;
  border-bottom: 1px solid #e9eaed;
`;

const Title = styled.span`
  font-family: Roboto-m;
  font-size: 2rem;
  color: #373333;
`;

const CloseButton = styled.img`
  width: 1.6rem;
  height: 1.6rem;
  cursor: pointer;
  opacity: 0.6;
  &:hover {
    opacity: 1;
  }
`;

const Body = styled.div`
  padding: 2.8rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
`;

const DropZone = styled.div`
  width: 100%;
  min-height: 200px;
  border: 2px dashed ${({ isDragging }) => (isDragging ? '#00ff91' : '#d2d1d1')};
  border-radius: 12px;
  background: ${({ isDragging }) => (isDragging ? '#00ff910d' : '#fafafa')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    border-color: #00ff91;
    background: #00ff910d;
  }
`;

const DropZoneIcon = styled.img`
  width: 4.8rem;
  height: 4.8rem;
  opacity: 0.4;
  margin-bottom: 1.6rem;
`;

const DropZoneText = styled.span`
  font-family: Roboto-r;
  font-size: 1.6rem;
  color: #4a4a4a;
  margin-bottom: 0.8rem;
`;

const DropZoneSubText = styled.span`
  font-family: Roboto-r;
  font-size: 1.2rem;
  color: #969494;
`;

const PdfCard = styled.div`
  width: 100%;
  padding: 2rem 2.4rem;
  background: #f7f6f6;
  border-radius: 12px;
  display: flex;
  align-items: center;
  margin-bottom: 2.4rem;
`;

const PdfIconWrapper = styled.div`
  width: 4.8rem;
  height: 4.8rem;
  min-width: 4.8rem;
  border-radius: 10px;
  background: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 1.6rem;
  border: 1px solid #e9eaed;
`;

const PdfIcon = styled.img`
  width: 2.4rem;
  height: 2.4rem;
`;

const PdfDetails = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const PdfName = styled.a`
  font-family: Roboto-m;
  font-size: 1.4rem;
  color: #373333;
  text-decoration: none;
  word-break: break-all;
  line-height: 2rem;
  &:hover {
    text-decoration: underline;
    color: #00c96d;
  }
`;

const PdfType = styled.span`
  font-family: Roboto-r;
  font-size: 1.2rem;
  color: #969494;
  margin-top: 0.2rem;
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 1.2rem;
  width: 100%;
`;

const LoadingWrapper = styled.div`
  width: 100%;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const LoadingText = styled.span`
  font-family: Roboto-r;
  font-size: 1.6rem;
  color: #4a4a4a;
`;

const LoadingBar = styled.div`
  width: 200px;
  height: 4px;
  background: #e9eaed;
  border-radius: 2px;
  margin-top: 1.6rem;
  overflow: hidden;
  &::after {
    content: '';
    display: block;
    width: 40%;
    height: 100%;
    background: #00ff91;
    border-radius: 2px;
    animation: loading 1.2s ease-in-out infinite;
  }
  @keyframes loading {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(350%); }
  }
`;

const NutritionPdfModal = ({ isOpen, closeModal, traineeUid, nutritionPdf, onSuccess }) => {
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const [uploadNutritionPdf] = useMutation(uploadNutritionPdfMutation);

  const handleUpload = useCallback(
    async (file) => {
      if (!file || !traineeUid) return;

      setUploading(true);
      try {
        await uploadNutritionPdf({
          variables: {
            record: {
              file,
              traineeUid,
            },
          },
        });
        if (onSuccess) onSuccess();
      } catch (err) {
        console.error('Failed to upload nutrition PDF:', err);
      }
      setUploading(false);
    },
    [traineeUid, uploadNutritionPdf, onSuccess],
  );

  const handleFileChange = useCallback(
    (e) => {
      var selectedFile = e.target.files && e.target.files[0];
      if (selectedFile) {
        handleUpload(selectedFile);
      }
    },
    [handleUpload],
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      var droppedFile = e.dataTransfer.files && e.dataTransfer.files[0];
      if (droppedFile) {
        handleUpload(droppedFile);
      }
    },
    [handleUpload],
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleClear = useCallback(async () => {
    if (!traineeUid) return;

    setUploading(true);
    try {
      await uploadNutritionPdf({
        variables: {
          record: {
            traineeUid,
          },
        },
      });
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Failed to clear nutrition PDF:', err);
    }
    setUploading(false);
  }, [traineeUid, uploadNutritionPdf, onSuccess]);

  return (
    <Modal onRequestClose={closeModal} isOpen={!!isOpen} style={customStyles}>
      <Wrapper>
        <Header>
          <Title>Nutrition</Title>
          <CloseButton src={ICONS.X} alt="close" onClick={closeModal} />
        </Header>
        <Body>
          {uploading ? (
            <LoadingWrapper>
              <LoadingText>Uploading PDF...</LoadingText>
              <LoadingBar />
            </LoadingWrapper>
          ) : nutritionPdf && nutritionPdf.url ? (
            <>
              <PdfCard>
                <PdfIconWrapper>
                  <PdfIcon src={ICONS.File} alt="pdf" />
                </PdfIconWrapper>
                <PdfDetails>
                  <PdfName href={nutritionPdf.url} target="_blank" rel="noopener noreferrer">
                    {nutritionPdf.filename || 'Nutrition'}
                  </PdfName>
                  <PdfType>PDF Document</PdfType>
                </PdfDetails>
              </PdfCard>
              <ButtonsRow>
                <div style={{ flex: 1 }}>
                  <CustomButton
                    outlined
                    borderRadius="8px"
                    hoverBackground="#F50A4Faa"
                    outlineColor="#F50A4Faa"
                    backgroundColor="#ffffff"
                    color="#373333"
                    height="4.2rem"
                    fontSize="1.4rem"
                    onClick={handleClear}
                  >
                    Delete
                  </CustomButton>
                </div>
                <div style={{ flex: 1 }}>
                  <CustomButton
                    outlined
                    borderRadius="8px"
                    hoverBackground="#00ff91"
                    outlineColor="#F1F0F0"
                    backgroundColor="#ffffff"
                    color="#373333"
                    height="4.2rem"
                    fontSize="1.4rem"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  >
                    Replace
                  </CustomButton>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                </div>
              </ButtonsRow>
            </>
          ) : (
            <DropZone
              isDragging={isDragging}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <DropZoneIcon src={ICONS.Upload} alt="upload" />
              <DropZoneText>Drag & drop your PDF here</DropZoneText>
              <DropZoneSubText>or click to browse files</DropZoneSubText>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </DropZone>
          )}
        </Body>
      </Wrapper>
    </Modal>
  );
};

export default NutritionPdfModal;
